import { useApiMutation, useApiQuery } from "@gladis/api-client";
import { InviteMemberForm } from "@gladis/domain-identity/ui";
import type { CurrentUserDto, HouseholdDto } from "@gladis/shared";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { AuthPage } from "../../components/auth-page.js";
import { Button, Label, TextField } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";

export const Route = createFileRoute("/_authenticated/onboarding")({
  component: Onboarding,
});

/**
 * Onboarding (FR-017 – FR-019, FR-044 – FR-046).
 *
 * Reached automatically by anyone with no active membership, from any in-app URL (AC-H1). Naming a
 * household is the ONE mandatory step; everything after it is skippable and none of it blocks
 * arrival at the dashboard.
 *
 * IT SERVES TWO PEOPLE, and 005 is what made that true. A first-time user arrives here because the
 * guard sent them. Someone who already has a household arrives deliberately, from Household
 * Settings, to create a SECOND one (FR-046) — which spec 003 deferred to this feature.
 *
 * That supersedes the second sentence of 003's FR-020, which said re-entering onboarding must hide
 * the create-a-household step. It was written for a product where nobody could have two households.
 * The intent behind it — do not force a returning member back through first-run setup — still holds
 * and is preserved below: they get different copy, a way out, and no pretence that this is setup.
 */
function Onboarding(): ReactNode {
  const [step, setStep] = useState<"create" | "optional">("create");
  const [created, setCreated] = useState<HouseholdDto | null>(null);
  const navigate = useNavigate();

  const me = useApiQuery<CurrentUserDto>(["me"], "/me");
  // Read ONCE, before anything is created, so the copy does not change under the user the moment
  // their new household lands.
  const [isFirstHousehold] = useState(() => (me.data?.households.length ?? 0) === 0);

  if (step === "create") {
    return (
      <CreateHouseholdStep
        isFirstHousehold={isFirstHousehold}
        onCreated={(household) => {
          setCreated(household);
          setStep("optional");
        }}
        onCancel={() => void navigate({ to: "/", replace: true })}
      />
    );
  }

  return (
    <OptionalSteps household={created} onFinish={() => void navigate({ to: "/", replace: true })} />
  );
}

function CreateHouseholdStep({
  isFirstHousehold,
  onCreated,
  onCancel,
}: {
  isFirstHousehold: boolean;
  onCreated: (household: HouseholdDto) => void;
  onCancel: () => void;
}): ReactNode {
  const t = useT();
  const [name, setName] = useState("");

  const create = useApiMutation<HouseholdDto, { name: string }>(
    (client, input) => client.post<HouseholdDto>("/households", input),
    {
      // The route guard reads /api/me to decide where someone belongs, so it must see the new
      // membership before we move on — otherwise it bounces straight back here.
      invalidates: [["me"]],
      onSuccess: onCreated,
    },
  );

  const trimmed = name.trim();

  return (
    <AuthPage>
      <h1 className="text-2xl font-semibold tracking-tight">
        {isFirstHousehold ? t("onboarding.heading") : t("onboarding.createAnother")}
      </h1>
      <p className="mt-2 text-sm text-muted">
        {isFirstHousehold ? t("onboarding.hint") : t("onboarding.anotherHint")}
      </p>

      <form
        className="mt-8"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate({ name: trimmed });
        }}
      >
        <Label htmlFor="household-name">{t("identity.household.name")}</Label>
        <TextField
          id="household-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />
        {/* Disabled on a whitespace-only name so the rejected submission never leaves the browser.
            The server enforces the same rule regardless — this is courtesy, not the guarantee. */}
        <Button
          variant="primary"
          className="mt-6 w-full"
          type="submit"
          disabled={trimmed.length === 0 || create.isPending}
        >
          {create.isPending ? t("onboarding.creating") : t("onboarding.create")}
        </Button>
      </form>

      {/* Only for someone who already has one. A first-time user has nowhere to go back TO — the
          guard would return them here immediately, and a control that undoes itself is worse than
          no control. */}
      {isFirstHousehold ? null : (
        <Button className="mt-3 w-full" type="button" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
      )}

      {create.isError ? (
        <p className="mt-4 text-sm text-muted" role="alert">
          {errorSentence(create.error)}
        </p>
      ) : null}
    </AuthPage>
  );
}

/**
 * The optional steps (FR-044, FR-045).
 *
 * INVITING IS NOW REAL. Feature 005 gave the identity package a form that knows what an invite is,
 * so the wizard composes it rather than describing it — which is the difference between a step and
 * a promise.
 *
 * The three pillar steps are still honest placeholders: savings (006), properties (008) and
 * expenses (009) do not exist yet. FR-045 asks for them to read as FORTHCOMING rather than broken,
 * and — the part that matters — never to block completion. Every one of them is skipped by the same
 * single action, because a wizard that makes you dismiss four things individually is a wizard
 * people learn to distrust.
 */
function OptionalSteps({
  household,
  onFinish,
}: {
  household: HouseholdDto | null;
  onFinish: () => void;
}): ReactNode {
  const t = useT();
  const forthcoming = [
    {
      title: t("savings.form.heading"),
      detail: t("onboarding.savingsHint"),
      feature: t("nav.savings"),
    },
    {
      title: t("prop.form.addHeading"),
      detail: t("onboarding.propertiesHint"),
      feature: t("nav.properties"),
    },
    {
      title: t("exp.form.addHeading"),
      detail: t("onboarding.expensesHint"),
      feature: t("nav.expenses"),
    },
  ];

  return (
    <AuthPage>
      {/* The heading stays fixed. Naming the household here instead of in the title confirms what
          was just created without making the step's marker depend on user input — 003's E2E uses
          this heading to assert the wizard advanced, and a heading that varies is a worse anchor
          for that than a subtitle is. */}
      <h1 className="text-2xl font-semibold tracking-tight">{t("onboarding.done")}</h1>
      <p className="mt-2 text-sm text-muted">
        {household ? `${household.name} is ready. ` : ""}
        Invite the rest of the family now if you like. Nothing here is required.
      </p>

      {household ? (
        <div className="mt-8">
          <InviteMemberForm householdId={household.id} />
        </div>
      ) : null}

      <ul className="mt-8 space-y-3">
        {forthcoming.map((item) => (
          <li
            key={item.title}
            className="rounded-control border border-input bg-input/30 px-4 py-3 text-sm"
          >
            <strong className="font-medium">{item.title}</strong>{" "}
            <em className="text-muted not-italic">(coming with {item.feature})</em>
            <span className="mt-1 block text-muted">{item.detail}</span>
          </li>
        ))}
      </ul>

      <Button variant="primary" className="mt-8 w-full" type="button" onClick={onFinish}>
        {t("onboarding.skip")}
      </Button>
    </AuthPage>
  );
}
